/**
 * My day — the member's whole Today (guide §5.1) and the associate director's My day. Prototype `day`
 * (docs/prototype.html 3056, member override 3884): greeting, the week strip, four tiles, "Needs you"
 * (members), Today's plan with time and length per task, the Schedule timeline, and the timesheet for anyone
 * who files one (`timesheet.file`).
 *
 * Data: GET /me/today (members, `today.own`) or GET /me/plan (everyone else); GET /tasks for due dates and the
 * week strip; POST /me/plan + /me/plan/confirm; POST /me/day-status per task; GET + PUT /me/timesheet.
 * Step 4: the "Feedback on saving time" card (GET /me/today → feedback, prototype `_vd10`: the two latest, above
 * Needs you) and optional holidays on the member's own timesheet (ADR-005 §4.1: GET/POST/DELETE /me/optional-holidays).
 * Calendar meetings need the Google Calendar contract (t-aeba292b) — until then the schedule says so plainly.
 */
import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useSearchParams } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { HEAD_PRIORITY_LABEL, type DayStatus, type Exception, type FileTimesheetBody, type ManagerFeedback, type PlanSlot, type Task } from '@amplispot/contracts';
import { ApiFailure, call, newIdempotencyKey } from '../../api';
import { dayLabel, firstName, todayIso } from '../../lib/format';
import { useSession } from '../../lib/session';
import { dayMonth, shortDay, useMyOptional, useTsWrite } from '../../lib/timesheets';
import { addDays, clock, dueText, duration, refreshAfterWrite, useAllTasks, useMeToday, useMyTimesheet, usePlan, usePlanSave, wkeys } from '../../lib/work';
import { Icon } from '../../ui/Icon';
import { ErrorState, Loading, errorText, useToast } from '../../ui/kit';
import { ClientLogo } from '../clients/parts';
import { AddTaskModal, CloseWithProofModal } from '../work/TaskModals';
import { LogMeetingModal } from '../clients/Meetings';
import { greeting } from './ManagerToday';

const STARTS = Array.from({ length: 39 }, (_, i) => 540 + i * 15); // 9:00 … 18:30
const LENGTHS = [15, 30, 45, 60, 90, 120, 180];
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function MyDay() {
  const { me, can, readOnly } = useSession();
  const member = can('today.own');
  const files = can('timesheet.file');
  const today = todayIso();
  const meToday = useMeToday(member);
  const planQ = usePlan(undefined, !member);
  // My own open tasks only (managers honour assignee_id; a member's list is filtered to theirs below too).
  const tasks = useAllTasks({ status: 'open', assignee_id: me.user.id });
  const plan = member ? meToday.data?.plan : planQ.data;
  const [, setSp] = useSearchParams();
  const [adding, setAdding] = useState(false);
  const [logging, setLogging] = useState(false);
  /** Rows answered so far in today's timesheet (Done / Partly / Not done), for the tile's "2 of 6 filled". */
  const [filled, setFilled] = useState(0);
  const planRef = useRef<HTMLDivElement>(null);
  const tsRef = useRef<HTMLDivElement>(null);
  const needRef = useRef<HTMLDivElement>(null);
  const toast = useToast();
  const qc = useQueryClient();
  const confirm = useMutation({
    mutationFn: () => call('me.planConfirm', { body: {} }),
    onSuccess: () => {
      toast('Plan confirmed');
      void qc.invalidateQueries({ queryKey: ['work', 'plan'] });
      void qc.invalidateQueries({ queryKey: wkeys.meToday });
    },
    onError: (e) => toast(errorText(e)),
  });

  const mine = useMemo(() => (tasks.data ?? []).filter((t) => t.assignee?.id === me.user.id), [tasks.data, me.user.id]);
  const byId = useMemo(() => new Map(mine.map((t) => [t.id, t])), [mine]);

  const q = member ? meToday : planQ;
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  if (!plan) return null;

  const slots = plan.slots;
  const needN = meToday.data?.tiles.needs_you_count ?? 0;
  const planned = slots.reduce((a, s) => a + s.minutes, 0);
  const first = slots.filter((s) => s.head_priority === 'first' && !s.done).length;
  const overdue = mine.filter((t) => t.overdue).length;
  const open = (id: string) =>
    setSp((p) => {
      const n = new URLSearchParams(p);
      n.set('task', id);
      return n;
    });
  const scroll = (r: RefObject<HTMLDivElement | null>) => r.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  // Mon–Fri of this week, with how many of my open tasks fall due each day.
  const dow = new Date(`${today}T00:00:00Z`).getUTCDay();
  const monday = addDays(today, dow === 0 ? 1 : 1 - dow);
  const week = [0, 1, 2, 3, 4].map((i) => addDays(monday, i));

  return (
    <>
      <div style={{ margin: '0 0 16px' }}>
        <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-.02em' }}>
          {greeting()}, {firstName(me.user.name)}
        </div>
        <div className="sub" style={{ marginTop: 2 }}>
          {dayLabel(today)}. {slots.length} {slots.length === 1 ? 'task' : 'tasks'} in today’s plan, in the order the Ops associate director set.
        </div>
      </div>

      <div className="days">
        {week.map((d) => {
          const n = mine.filter((t) => t.due === d).length;
          return (
            <div key={d} className={`dpill${d === today ? ' on' : ''}`}>
              <small>{DOW[new Date(`${d}T00:00:00Z`).getUTCDay()]}</small>
              <b>{Number(d.slice(8))}</b>
              <span className="dt" title={tasks.error ? 'Your tasks did not load' : undefined}>
                {d === today ? 'Today' : tasks.isLoading ? '…' : tasks.error ? '-' : `${n} due`}
              </span>
            </div>
          );
        })}
      </div>

      <div className="tiles">
        <button className="tile or" onClick={() => scroll(planRef)}>
          <span className="ti">
            <Icon name="spark" />
          </span>
          <b>{plan.confirmed_at ? 'Plan confirmed' : 'Confirm your plan'}</b>
          <small>
            {plan.confirmed_at
              ? `Locked in at ${clock(minutesOf(plan.confirmed_at))}`
              : `${slots.length} ${slots.length === 1 ? 'task' : 'tasks'}, ${Math.floor(planned / 60)}h ${planned % 60}m. Confirm before 10 am`}
          </small>
          <span className="go">→</span>
        </button>
        {files ? <TimesheetTile slots={slots} filled={filled} onGo={() => scroll(tsRef)} /> : null}
        <button className="tile te" onClick={() => scroll(planRef)}>
          <span className="ti">
            <Icon name="users" />
          </span>
          <b>{first} to do first</b>
          <small>Set by the Ops associate director. Do these before anything else</small>
          <span className="go">→</span>
        </button>
        {member && meToday.data ? (
          <button className="tile dk" onClick={() => scroll(needRef)}>
            <span className="ti">
              <Icon name="alert" />
            </span>
            <b>{needN ? `${needN} ${needN === 1 ? 'needs' : 'need'} you` : 'Nothing needs you'}</b>
            <small>{needN ? 'Start here, most important first' : 'You are on top of it'}</small>
            <span className="go">→</span>
          </button>
        ) : (
          <a className="tile dk" href="/work/allwork?status=overdue" style={{ textDecoration: 'none' }}>
            <span className="ti">
              <Icon name="alert" />
            </span>
            {tasks.isLoading ? (
              <>
                <b>Checking your tasks…</b>
                <small>Overdue tasks show here</small>
              </>
            ) : tasks.error ? (
              <>
                <b>Tasks did not load</b>
                <small>Open All work to see what is overdue</small>
              </>
            ) : (
              <>
                <b>{overdue ? `${overdue} overdue` : 'Nothing overdue'}</b>
                <small>{overdue ? 'Move the date with a reason, or close it with proof' : 'You are on top of it'}</small>
              </>
            )}
            <span className="go">→</span>
          </a>
        )}
      </div>

      {member && meToday.data?.feedback.length ? <FeedbackCard items={meToday.data.feedback} /> : null}

      {member && meToday.data ? (
        <div ref={needRef} style={{ marginBottom: 26 }}>
          <div className="split" style={{ marginBottom: 12 }}>
            <h2 style={{ fontSize: 18, fontWeight: 800 }}>Needs you</h2>
            <span className={`chip ${needN ? 'cold' : 'hot'}`}>{needN ? `${needN} waiting` : 'All clear'}</span>
            <div className="grow" />
            {/* Prototype member Today hdr: Log a meeting (newMeet), then Add a task (newTaskModal). */}
            <button className="btn sm" onClick={() => setLogging(true)} disabled={readOnly} title={readOnly ? 'Read-only while you view as someone' : undefined}>
              Log a meeting
            </button>
            <button className="btn sm" onClick={() => setAdding(true)}>
              Add a task
            </button>
          </div>
          {meToday.data.needs_you.length ? (
            <div className="sub" style={{ margin: '-4px 0 12px', fontSize: 13 }}>
              Most important clients first. Press one to deal with it.
            </div>
          ) : null}
          <div className="panel">
            {meToday.data.needs_you.length ? (
              meToday.data.needs_you.map((e) => {
                const r = needRow(e, byId, today);
                return (
                <a
                  key={e.key}
                  className="it"
                  href={e.route}
                  onClick={(ev) => {
                    ev.preventDefault();
                    const u = new URL(e.route, location.origin);
                    const id = u.searchParams.get('task');
                    if (id) open(id);
                    else window.location.assign(e.route);
                  }}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <span style={{ flex: 'none' }}>{e.client ? <ClientLogo id={e.client.id} name={e.client.name} size={30} /> : <span style={{ width: 30, display: 'inline-block' }} />}</span>
                  <div className="grow" style={{ minWidth: 0 }}>
                    <div className="tt">{r.title}</div>
                    <div className="mm">{r.meta}</div>
                  </div>
                  <span className={`chip ${r.tone}`}>{r.chip}</span>
                </a>
                );
              })
            ) : (
              <div className="empty">
                <b>Nothing is waiting on you</b>Go on to today’s plan below.
              </div>
            )}
            {meToday.data.more_count ? (
              <a
                className="it"
                href="/work/allwork"
                style={{
                  justifyContent: 'center',
                  color: 'var(--brand)',
                  fontSize: 12.5,
                  fontWeight: 600,
                  textDecoration: 'none',
                }}
              >
                Show {meToday.data.more_count} more
              </a>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="cols c21" style={{ alignItems: 'start' }}>
        <div ref={planRef}>
          <div className="split" style={{ marginBottom: 12 }}>
            <h2 style={{ fontSize: 18, fontWeight: 800 }}>Today’s plan</h2>
            <div className="grow" />
            {plan.confirmed_at ? (
              <span className="chip hot">Confirmed {clock(minutesOf(plan.confirmed_at))}</span>
            ) : (
              <button className="btn pri" onClick={() => confirm.mutate()} disabled={confirm.isPending || !slots.length}>
                Confirm my plan
              </button>
            )}
          </div>
          <div className="sub" style={{ margin: '-4px 0 14px', fontSize: 13 }}>
            Your tasks in the order set by the Ops associate director. Adjust the time you will spend, then confirm.
          </div>
          {slots.length ? (
            slots.map((s) => <PlanCard key={s.task_id} s={s} task={byId.get(s.task_id)} today={today} onOpen={() => open(s.task_id)} />)
          ) : (
            <div className="panel">
              <div className="empty">
                <b>Nothing planned for today</b>Tasks due today or overdue land here in the order the Ops associate director set.
              </div>
            </div>
          )}
        </div>
        <Schedule slots={slots} planned={planned} onOpen={open} />
      </div>

      {files ? <Timesheet slots={slots} byId={byId} tsRef={tsRef} onFilled={setFilled} /> : null}
      {adding ? <AddTaskModal onClose={() => setAdding(false)} /> : null}
      {logging ? <LogMeetingModal onClose={() => setLogging(false)} /> : null}
    </>
  );
}

function minutesOf(ts: string): number {
  const p = new Intl.DateTimeFormat('en-GB', {
    hour: 'numeric',
    minute: 'numeric',
    hourCycle: 'h23',
    timeZone: 'Asia/Kolkata',
  }).formatToParts(new Date(ts));
  return Number(p.find((x) => x.type === 'hour')?.value) * 60 + Number(p.find((x) => x.type === 'minute')?.value);
}

// ---------------------------------------------------------------- plan card

function PlanCard({ s, task, today, onOpen }: { s: PlanSlot; task: Task | undefined; today: string; onOpen: () => void }) {
  const save = usePlanSave();
  const toast = useToast();
  const set = (start: number, minutes: number) => save.mutate({ [s.task_id]: { start_minute: start, minutes } }, { onError: (e) => toast(errorText(e)) });
  const due = task ? dueText(task.due, today) : s.overdue ? { label: 'Overdue', tone: 'cold' as const } : { label: 'Today', tone: 'warm' as const };
  const sel = { width: 'auto', padding: '6px 10px', borderRadius: 99 } as const;
  return (
    <div
      className="tcard"
      style={{
        marginBottom: 10,
        ...(s.head_priority === 'first' && !s.done ? { boxShadow: '0 12px 26px rgba(255,107,53,.18)' } : {}),
      }}
    >
      {s.client ? (
        <ClientLogo id={s.client.id} name={s.client.name} size={42} />
      ) : (
        <span
          className="clogo"
          style={{
            width: 42,
            height: 42,
            borderRadius: 10,
            background: '#64748B',
            fontSize: 14,
          }}
          aria-hidden="true"
        >
          IN
        </span>
      )}
      <div className="b">
        <div className="t" style={s.done ? { textDecoration: 'line-through', color: 'var(--mute)' } : undefined}>
          <a
            href={`?task=${s.task_id}`}
            onClick={(e) => {
              e.preventDefault();
              onOpen();
            }}
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            {s.title}
          </a>
        </div>
        <div className="m">
          <span>{s.client ? s.client.name : 'Internal'}</span>
          <span className={`rank ${s.head_priority}`}>{HEAD_PRIORITY_LABEL[s.head_priority]}</span>
          {s.done ? <span className="chip hot">Done</span> : <span className={`chip ${due.tone}`}>{due.label}</span>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <select className="in" style={sel} value={s.start_minute} onChange={(e) => set(Number(e.target.value), s.minutes)} aria-label="Start time" disabled={save.isPending}>
          {(STARTS.includes(s.start_minute) ? STARTS : [...STARTS, s.start_minute].sort((a, b) => a - b)).map((m) => (
            <option key={m} value={m}>
              {clock(m)}
            </option>
          ))}
        </select>
        <select className="in" style={sel} value={s.minutes} onChange={(e) => set(s.start_minute, Number(e.target.value))} aria-label="How long" disabled={save.isPending}>
          {(LENGTHS.includes(s.minutes) ? LENGTHS : [...LENGTHS, s.minutes].sort((a, b) => a - b)).map((m) => (
            <option key={m} value={m}>
              {duration(m)}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- schedule

function Schedule({ slots, planned, onOpen }: { slots: PlanSlot[]; planned: number; onOpen: (id: string) => void }) {
  const now = minutesOf(new Date().toISOString());
  const hours = Array.from({ length: 11 }, (_, i) => 9 + i);
  return (
    <div className="panel">
      <div className="hd">
        <h3>Schedule</h3>
        <div className="r">{(planned / 60).toFixed(1)} h planned</div>
      </div>
      <div className="tl" style={{ height: 600 }}>
        {hours.map((h) => (
          <div key={h}>
            <div className="ln" style={{ top: (h - 9) * 60 }} />
            <div className="hr" style={{ top: (h - 9) * 60 }}>
              {((h + 11) % 12) + 1} {h < 12 ? 'AM' : 'PM'}
            </div>
          </div>
        ))}
        {slots.map((s) => (
          <button
            key={s.task_id}
            className={`blk ${s.head_priority === 'first' && !s.done ? 'first' : ''} ${s.done ? 'dn' : ''}`}
            style={{
              top: Math.max(0, s.start_minute - 540),
              height: Math.max(s.minutes - 4, 30),
            }}
            onClick={() => onOpen(s.task_id)}
          >
            <b>{s.title}</b>
            <span>
              {clock(s.start_minute)} to {clock(s.start_minute + s.minutes)}
              {s.client ? `, ${s.client.name}` : ''}
            </span>
          </button>
        ))}
        {now >= 540 && now <= 1140 ? <div className="now" style={{ top: now - 540 }} /> : null}
      </div>
      <div className="note" style={{ margin: '0 16px 16px', fontSize: 12.5 }}>
        Your meetings are not shown yet. Once your Google Calendar is connected, they appear here beside your tasks.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- feedback

/** Prototype `_vd10`: a `.note` above Needs you with the two latest notes — category chip, the line, "by, date". */
function FeedbackCard({ items }: { items: ManagerFeedback[] }) {
  const latest = [...items].sort((a, b) => b.at.localeCompare(a.at)).slice(0, 2);
  return (
    <div className="note" style={{ marginBottom: 18 }}>
      <b>Feedback on saving time</b>
      {latest.map((f) => (
        <div key={f.id} style={{ marginTop: 8 }}>
          <span className="chip">{f.category}</span> {f.body}{' '}
          <span className="sub">
            {firstName(f.by.name)}, {dayMonth(f.at.slice(0, 10))}
          </span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- optional holidays

/**
 * ADR-005 §4.1: the member marks an optional holiday they took on their own timesheet; that day's expected hours
 * become 0, within HR's yearly allowance. Shown only when today or an earlier day still open to change is an
 * optional holiday (or one is already taken). Withdrawing can make an unfiled past day due again (`re_gated`).
 */
function OptionalHolidays({ today }: { today: string }) {
  const year = Number(today.slice(0, 4));
  const q = useMyOptional(year);
  const toast = useToast();
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const elect = useTsWrite((id: string) => call('me.optionalHolidaysElect', { body: { holiday_day_id: id }, idempotencyKey: newIdempotencyKey() }));
  const withdraw = useTsWrite((id: string) => call('me.optionalHolidaysWithdraw', { params: { id } }));
  if (q.isLoading || !q.data) {
    if (q.error && !(q.error instanceof ApiFailure && q.error.status === 404))
      return (
        <div className="note bad" style={{ marginBottom: 14 }}>
          Your optional holidays did not load. {errorText(q.error)}{' '}
          <button className="btn sm ghost" onClick={() => void q.refetch()}>
            Try again
          </button>
        </div>
      );
    return null;
  }
  const o = q.data;
  const rows = o.available
    .filter((a) => a.elected || (a.editable && a.day.the_date <= today))
    .sort((a, b) => b.day.the_date.localeCompare(a.day.the_date));
  if (!rows.length) return null;
  const busy = elect.isPending || withdraw.isPending;
  const label = (d: string) => (d === today ? 'Today' : shortDay(d));

  return (
    <div className="note" style={{ marginBottom: 14 }}>
      <div className="split" style={{ gap: 10, flexWrap: 'wrap' }}>
        <b>Optional holidays</b>
        <span className="sub">
          {o.used} of {o.allowance} used in {o.year}
          {o.remaining ? `, ${o.remaining} left` : ''}
        </span>
      </div>
      {rows.map((a) => {
        const d = a.day;
        return (
          <div key={d.id} className="split" style={{ marginTop: 8, gap: 10, flexWrap: 'wrap' }}>
            <span>
              <b>{label(d.the_date)}</b> is {d.name}, an optional holiday.
            </span>
            {a.elected ? <span className="chip hot">You took it off</span> : null}
            <div className="grow" />
            {a.elected && a.editable && a.election_id ? (
              confirmId === a.election_id ? (
                <>
                  <span className="sub">
                    {d.the_date < today ? `${label(d.the_date)} becomes a working day again. If you did not file it, the next day locks until you do.` : 'The day becomes a working day again.'}
                  </span>
                  <button
                    className="btn sm"
                    disabled={busy}
                    onClick={() =>
                      withdraw.mutate(a.election_id!, {
                        onSuccess: (r) => {
                          setConfirmId(null);
                          toast(r.re_gated ? `Withdrawn. File ${label(d.the_date)}’s timesheet to open the next day.` : `Withdrawn. ${label(d.the_date)} is a working day again.`);
                        },
                        onError: (e) => toast(errorText(e)),
                      })
                    }
                  >
                    Withdraw
                  </button>
                  <button className="btn sm ghost" onClick={() => setConfirmId(null)}>
                    Keep it
                  </button>
                </>
              ) : (
                <button className="btn sm ghost" onClick={() => setConfirmId(a.election_id)}>
                  Withdraw
                </button>
              )
            ) : !a.elected && a.editable ? (
              o.remaining > 0 ? (
                <button
                  className="btn sm pri"
                  disabled={busy}
                  onClick={() =>
                    elect.mutate(d.id, {
                      onSuccess: () => toast(`${d.name} taken off. ${label(d.the_date)} expects no hours now.`),
                      onError: (e) => toast(errorText(e)),
                    })
                  }
                >
                  I am taking {d.name} off
                </button>
              ) : (
                <span className="sub">All {o.allowance} optional days are used this year.</span>
              )
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------- timesheet

type Ans = { state?: DayStatus['state']; minutes: number; note: string };

/** Prototype day tile `vi`: clock icon, "Fill your timesheet" / "{filled} of {n} filled. Due by 6:30 pm". */
function TimesheetTile({ slots, filled, onGo }: { slots: PlanSlot[]; filled: number; onGo: () => void }) {
  const ts = useMyTimesheet(todayIso(), true);
  const filed = ts.data?.hours_filed != null;
  return (
    <button className="tile vi" onClick={onGo}>
      <span className="ti">
        <Icon name="clock" />
      </span>
      <b>{filed ? 'Timesheet submitted' : 'Fill your timesheet'}</b>
      <small>{filed ? `Thank you. ${ts.data!.hours_filed} h filed for today` : `${filled} of ${slots.length} filled. Due by 6:30 pm`}</small>
      <span className="go">→</span>
    </button>
  );
}

function Timesheet({
  slots,
  byId,
  tsRef,
  onFilled,
}: {
  slots: PlanSlot[];
  byId: Map<string, Task>;
  tsRef: RefObject<HTMLDivElement | null>;
  onFilled: (n: number) => void;
}) {
  const today = todayIso();
  const toast = useToast();
  const qc = useQueryClient();
  const ts = useMyTimesheet(today, true);
  const [ans, setAns] = useState<Record<string, Ans>>({});
  const [extras, setExtras] = useState<{ title: string; minutes: number }[]>([]);
  const [exT, setExT] = useState('');
  const [exM, setExM] = useState('');
  const [proofFor, setProofFor] = useState<Task | null>(null);
  // A slot whose task is already closed reads as Done; Partly / Not done have no read-back yet (backend FIX).
  const a = (s: PlanSlot): Ans => ans[s.task_id] ?? { state: s.done ? 'done' : undefined, minutes: s.minutes, note: '' };
  const patch = (id: string, p: Partial<Ans>, s: PlanSlot) => setAns((x) => ({ ...x, [id]: { ...a(s), ...p } }));
  const filledN = slots.filter((s) => a(s).state).length;
  useEffect(() => onFilled(filledN), [filledN, onFilled]);

  /** What the server last got for each row, so an edit made after picking Done/Partly/Not done is re-sent. */
  const sent = useRef<Record<string, string>>({});
  const sig = (x: Ans) => `${x.state}|${x.minutes}|${x.note.trim()}`;
  const sendStatus = (taskId: string, x: Ans & { state: DayStatus['state'] }) => {
    sent.current[taskId] = sig(x);
    return call('me.dayStatus', {
      body: { task_id: taskId, state: x.state, actual_minutes: Math.round(x.minutes), note: x.note.trim().slice(0, 500) || undefined },
    });
  };
  /** Re-send an answered row whose minutes or note changed since it was last sent (on blur, and before filing). */
  const sync = async (s: PlanSlot) => {
    const x = a(s);
    if (!x.state || !ans[s.task_id] || sent.current[s.task_id] === sig(x)) return;
    await sendStatus(s.task_id, { ...x, state: x.state });
  };
  const syncQuiet = (s: PlanSlot) => void sync(s).catch((e) => toast(errorText(e)));

  const status = useMutation({
    // The row's values are passed in, not read from state: the click that sets the state sends in the same tick.
    mutationFn: (v: { s: PlanSlot; x: Ans & { state: DayStatus['state'] } }) => sendStatus(v.s.task_id, v.x),
    onSuccess: async (r) => {
      if (r.requires_proof) {
        try {
          setProofFor(byId.get(r.task_id) ?? (await call('tasks.get', { params: { id: r.task_id } })));
        } catch (e) {
          toast(errorText(e));
        }
      }
    },
    onError: (e) => toast(errorText(e)),
  });

  const total = slots.reduce((x, s) => x + a(s).minutes, 0) + extras.reduce((x, e) => x + e.minutes, 0);
  const answered = slots.filter((s) => a(s).state).length;
  const filed = ts.data?.hours_filed != null;
  /** A locked day (week closed) can only be changed by a manager — the page shows it read-only. */
  const locked = ts.data?.locked === true;

  const file = useMutation({
    mutationFn: async () => {
      // Every answer the server has not seen yet (a note or minutes changed after the state was picked).
      await Promise.all(slots.map(sync));
      const per = new Map<string, { m: number; notes: string[] }>();
      const internal = { m: 0, notes: [] as string[] };
      for (const s of slots) {
        const x = a(s);
        const g = s.client ? (per.get(s.client.id) ?? { m: 0, notes: [] }) : internal;
        g.m += x.minutes;
        if (x.note.trim()) g.notes.push(`${s.title}: ${x.note.trim()}`);
        if (s.client) per.set(s.client.id, g);
      }
      // Work that was not in the plan keeps what the person typed, with its minutes.
      for (const e of extras) {
        internal.m += e.minutes;
        if (e.title.trim()) internal.notes.push(`${e.title.trim()} (${e.minutes} min)`);
      }
      const q = (m: number) => Math.round((m / 60) * 4) / 4;
      const note = (n: string[]) => (n.length ? n.join('; ').slice(0, 300) : undefined);
      const entries: FileTimesheetBody['entries'] = [...per]
        .map(([client_id, g]) => ({ client_id, hours: q(g.m), note: note(g.notes) }))
        .filter((e) => e.hours >= 0.25);
      if (q(internal.m) >= 0.25) entries.push({ work_type: 'Internal work', hours: q(internal.m), note: note(internal.notes) });
      const hours_filed = Math.round(entries.reduce((x, e) => x + e.hours, 0) * 100) / 100;
      return call('me.timesheetFile', {
        params: { date: today },
        body: { hours_filed, entries },
        ifMatch: ts.data?.version ?? undefined,
      });
    },
    onSuccess: (d) => {
      toast(`Timesheet filed: ${d.hours_filed} h`);
      refreshAfterWrite(qc);
    },
    onError: (e) => toast(e instanceof ApiFailure && e.status === 409 ? 'This day is locked. Ask the Ops associate director to change it.' : errorText(e)),
  });
  const submit = () => {
    if (locked) return toast('This day is locked. Only a manager can change it now.');
    if (answered < slots.length) return toast(`Say how each task went first (${answered} of ${slots.length} done)`);
    file.mutate();
  };

  return (
    <div id="tsBox" ref={tsRef} style={{ marginTop: 28 }}>
      <OptionalHolidays today={today} />
      <div className="ts-hero">
        <div className="grow">
          <h2>Today’s timesheet</h2>
          <p>
            {locked
              ? `This day is locked${filed ? ` — ${ts.data!.hours_filed} h filed` : ''}. Only a manager can change this now.`
              : filed
                ? `Filed ${ts.data!.filed_at ? 'at ' + clock(minutesOf(ts.data!.filed_at)) : ''}. You can update it until the day is locked.`
                : ts.data?.expected_hours === 0
                  ? 'Today is a holiday for you, so nothing is due. File it only if you worked.'
                  : 'Record how long each task really took. It takes two minutes, and it is due by 6:30 pm.'}
          </p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 30, fontWeight: 800, lineHeight: 1 }}>{filed ? `${ts.data!.hours_filed}h` : duration(total).replace(' min', 'm')}</div>
          <div style={{ fontSize: 12.5, opacity: 0.9 }}>logged of {ts.data?.expected_hours ?? 7.5}h</div>
        </div>
        <button className="btn" onClick={submit} disabled={file.isPending || locked}>
          {filed ? 'Update timesheet' : 'Submit timesheet'}
        </button>
      </div>
      <div className="panel">
        <table>
          <thead>
            <tr>
              <th>Task</th>
              <th>Planned</th>
              <th>Actual minutes</th>
              <th>How did it go</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody>
            {slots.map((s) => (
              <tr key={s.task_id}>
                <td>
                  <div className="split">
                    {s.client ? <ClientLogo id={s.client.id} name={s.client.name} size={30} /> : null}
                    <div>
                      <b>{s.title}</b>
                      <div className="sub">{s.client ? s.client.name : 'Internal'}</div>
                    </div>
                  </div>
                </td>
                <td className="num">{s.minutes} min</td>
                <td>
                  <input
                    className="in mins"
                    type="number"
                    min={0}
                    step={5}
                    disabled={locked}
                    value={a(s).minutes}
                    onChange={(e) => patch(s.task_id, { minutes: Math.max(0, Number(e.target.value) || 0) }, s)}
                    onBlur={() => syncQuiet(s)}
                    aria-label={`Actual minutes for ${s.title}`}
                  />
                </td>
                <td>
                  <div className="seg">
                    {(
                      [
                        ['done', 'Done'],
                        ['part', 'Partly'],
                        ['no', 'Not done'],
                      ] as const
                    ).map(([k, l]) => (
                      <button
                        key={k}
                        className={a(s).state === k ? 'on' : ''}
                        aria-pressed={a(s).state === k}
                        disabled={locked}
                        onClick={() => {
                          const x = { ...a(s), state: k };
                          patch(s.task_id, { state: k }, s);
                          status.mutate({ s, x });
                        }}
                      >
                        {l}
                      </button>
                    ))}
                  </div>
                </td>
                <td>
                  <input className="in" style={{ minWidth: 150 }} placeholder="Optional" disabled={locked} value={a(s).note} onChange={(e) => patch(s.task_id, { note: e.target.value }, s)} onBlur={() => syncQuiet(s)} />
                </td>
              </tr>
            ))}
            {extras.map((x, i) => (
              <tr key={i}>
                <td colSpan={2}>
                  <input className="in" value={x.title} onChange={(e) => setExtras((l) => l.map((y, j) => (j === i ? { ...y, title: e.target.value } : y)))} />
                  <div className="sub" style={{ marginTop: 3 }}>
                    Not in the plan
                  </div>
                </td>
                <td>
                  <input
                    className="in mins"
                    type="number"
                    min={0}
                    step={5}
                    value={x.minutes}
                    onChange={(e) =>
                      setExtras((l) =>
                        l.map((y, j) =>
                          j === i
                            ? {
                                ...y,
                                minutes: Math.max(0, Number(e.target.value) || 0),
                              }
                            : y,
                        ),
                      )
                    }
                  />
                </td>
                <td />
                <td>
                  <button className="btn sm ghost" onClick={() => setExtras((l) => l.filter((_, j) => j !== i))}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
            {locked ? null : (
              <tr>
                <td colSpan={5}>
                  <div className="split">
                    <input className="in" placeholder="Something else you did that was not in the plan" value={exT} onChange={(e) => setExT(e.target.value)} />
                    <input className="in mins" type="number" placeholder="Min" value={exM} onChange={(e) => setExM(e.target.value)} />
                    <button
                      className="btn sm"
                      onClick={() => {
                        if (!exT.trim() || !(Number(exM) > 0)) return toast('Say what it was and how many minutes');
                        setExtras((l) => [...l, { title: exT.trim(), minutes: Number(exM) }]);
                        setExT('');
                        setExM('');
                      }}
                    >
                      Add
                    </button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <div className="bd" style={{ borderTop: '1px solid var(--line)' }}>
          <div className="split">
            <span className="sub">{locked ? 'Only a manager can change this now.' : 'Client hours are grouped by client; anything else is filed as internal work.'}</span>
            <div className="grow" />
            <button className="btn pri" onClick={submit} disabled={file.isPending || locked}>
              {filed ? 'Update timesheet' : 'Submit timesheet'}
            </button>
          </div>
        </div>
      </div>
      {!filed && !locked ? (
        <button
          className="btn pri"
          onClick={() => tsRef.current?.scrollIntoView({ behavior: 'smooth' })}
          style={{
            position: 'fixed',
            right: 28,
            bottom: 26,
            zIndex: 40,
            padding: '14px 22px',
            background: 'linear-gradient(135deg,#FF9160,#FF6B35)',
            boxShadow: '0 14px 28px rgba(255,107,53,.45)',
          }}
        >
          <Icon name="clock" />
          Fill today’s timesheet
          <span className="chip" style={{ background: 'rgba(255,255,255,.25)', color: '#fff' }}>
            {answered}/{slots.length}
          </span>
        </button>
      ) : null}
      {proofFor ? <CloseWithProofModal task={proofFor} onClose={() => setProofFor(null)} /> : null}
    </div>
  );
}

/**
 * One Needs you row as the prototype memberNeeds draws it: the plain title, "Client. meta" under it and the row's
 * status as the chip ("Overdue 1d", "Today", "Blocked 4d"), not its button. A row about one of my own tasks
 * (overdue, blocked) reads all three from that task, so the title carries no "Overdue:" / "Blocked N days:"
 * prefix; the other kinds (change asked, requests, a missing timesheet) keep the server's words, and their one
 * action as the chip when the contract gives them no status.
 */
function needRow(e: Exception, byId: Map<string, Task>, today: string): { title: string; meta: string; chip: string; tone: string } {
  const id = new URL(e.route, 'http://x').searchParams.get('task');
  const t = id && (e.kind === 'overdue' || e.kind === 'blk') ? byId.get(id) : undefined;
  const where = (m: string) => (e.client ? `${e.client.name}. ${m}` : m);
  const band = e.band === 'at_risk' ? 'cold' : 'warm';
  if (!t) return { title: e.title, meta: where(e.reason), chip: e.action_label, tone: band };
  const who = (t.assignee ? firstName(t.assignee.name) : 'Unassigned') + (t.blocked ? `, blocked on ${t.blocked.on}` : '');
  if (e.kind === 'blk' && t.blocked) return { title: t.title, meta: where(who), chip: `Blocked ${t.blocked.days}d`, tone: t.blocked.flagged ? 'cold' : 'warm' };
  const d = t.due ? dueText(t.due, today) : null;
  return { title: t.title, meta: where(who), chip: d ? d.label : e.action_label, tone: d ? d.tone : band };
}
